import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  VpnKey as VpnKeyIcon,
  ContentCopy as CopyIcon,
  Block as BlockIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { apiKeysAPI } from '../services/api';

function formatFecha(fechaStr) {
  if (!fechaStr) return '—';
  const d = new Date(fechaStr);
  return d.toLocaleDateString('es-AR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function ApiKeys() {
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [nombreInput, setNombreInput] = useState('');
  const [nombreError, setNombreError] = useState('');

  const [revealedKey, setRevealedKey] = useState(null);
  const [revealDialogOpen, setRevealDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, nombre: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const { data: keys, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: async () => {
      const res = await apiKeysAPI.listar();
      return res.data.data;
    },
  });

  const crearMutation = useMutation({
    mutationFn: apiKeysAPI.crear,
    onSuccess: (res) => {
      queryClient.invalidateQueries(['api-keys']);
      setCreateDialogOpen(false);
      setNombreInput('');
      setNombreError('');
      setRevealedKey({ nombre: res.data.data.nombre, clave: res.data.data.clave });
      setRevealDialogOpen(true);
    },
    onError: (err) => {
      showSnackbar(err.response?.data?.error?.message || 'Error al crear la API Key', 'error');
    },
  });

  const revocarMutation = useMutation({
    mutationFn: apiKeysAPI.revocar,
    onSuccess: () => {
      queryClient.invalidateQueries(['api-keys']);
      showSnackbar('API Key revocada', 'warning');
    },
    onError: (err) => {
      showSnackbar(err.response?.data?.error?.message || 'Error al revocar', 'error');
    },
  });

  const eliminarMutation = useMutation({
    mutationFn: apiKeysAPI.eliminar,
    onSuccess: () => {
      queryClient.invalidateQueries(['api-keys']);
      setDeleteConfirm({ open: false, id: null, nombre: '' });
      showSnackbar('API Key eliminada', 'success');
    },
    onError: (err) => {
      showSnackbar(err.response?.data?.error?.message || 'Error al eliminar', 'error');
    },
  });

  const handleCreate = () => {
    if (!nombreInput.trim()) {
      setNombreError('El nombre es requerido');
      return;
    }
    crearMutation.mutate({ nombre: nombreInput.trim() });
  };

  const handleCopy = () => {
    if (revealedKey?.clave) {
      navigator.clipboard.writeText(revealedKey.clave);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNombreInput('');
    setNombreError('');
  };

  const handleCloseRevealDialog = () => {
    setRevealDialogOpen(false);
    setRevealedKey(null);
    setCopied(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKeyIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4">API Keys</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
          Nueva API Key
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Las API Keys permiten a aplicaciones de terceros realizar reservas, anulaciones y registros
        en nombre de tu organización. Cada clave se muestra <strong>una sola vez</strong> al crearla
        — guardala en un lugar seguro.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Creada</TableCell>
              <TableCell>Último uso</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : !keys?.length ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    No hay API Keys registradas
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              keys.map((k) => (
                <TableRow key={k.Id} hover>
                  <TableCell>{k.Nombre}</TableCell>
                  <TableCell>{formatFecha(k.CreadoEn)}</TableCell>
                  <TableCell>{formatFecha(k.UltimoUso)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={k.Activo ? 'Activa' : 'Revocada'}
                      color={k.Activo ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {k.Activo ? (
                      <Tooltip title="Revocar (deshabilitar sin eliminar)">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => revocarMutation.mutate(k.Id)}
                          disabled={revocarMutation.isPending}
                        >
                          <BlockIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <Tooltip title="Eliminar permanentemente">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setDeleteConfirm({ open: true, id: k.Id, nombre: k.Nombre })
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog: crear nueva key */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva API Key</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nombre descriptivo *"
            placeholder="Ej: App Municipio, Portal Web, Kiosco..."
            value={nombreInput}
            onChange={(e) => {
              setNombreInput(e.target.value);
              setNombreError('');
            }}
            error={!!nombreError}
            helperText={nombreError}
            sx={{ mt: 2 }}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog} startIcon={<CancelIcon />}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={crearMutation.isPending}
          >
            {crearMutation.isPending ? 'Generando...' : 'Generar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: mostrar key generada (una sola vez) */}
      <Dialog open={revealDialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>API Key generada — {revealedKey?.nombre}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Copiá la clave ahora.</strong> Esta es la única vez que se muestra. Una vez
            cerrada esta ventana, no se puede recuperar.
          </Alert>
          <TextField
            fullWidth
            value={revealedKey?.clave || ''}
            inputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: 13 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? '¡Copiado!' : 'Copiar al portapapeles'}>
                    <IconButton onClick={handleCopy} edge="end" color={copied ? 'success' : 'default'}>
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRevealDialog} variant="contained">
            Entendido, cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: confirmar eliminación */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null, nombre: '' })}
      >
        <DialogTitle>Eliminar API Key</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Eliminar la API Key <strong>{deleteConfirm.nombre}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Las aplicaciones que la usen dejarán de funcionar inmediatamente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, id: null, nombre: '' })}>
            Cancelar
          </Button>
          <Button
            onClick={() => eliminarMutation.mutate(deleteConfirm.id)}
            color="error"
            variant="contained"
            disabled={eliminarMutation.isPending}
          >
            {eliminarMutation.isPending ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
